import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import I18n from 'i18n-js';
import cx from 'classnames';
import { Popover } from 'grab-ui';
import InputField from 'components/v2/input-field/input-field';
import SelectField from 'components/v2/select-field/select-field';
import DummyFieldError from 'components/v2/dummy-field-error/dummy-field-error';
import { deliveryFormFields } from 'views/v2/new-booking-page/delivery-form/common';
import {
  COLUMN_SENDER_ADDRESS,
  COLUMN_RECEIVER_ADDRESS,
  COLUMN_SENDER_PHONE,
  COLUMN_SENDER_NAME,
  COLUMN_RECEIVER_NAME,
  COLUMN_RECEIVER_PHONE,
  COLUMN_SENDER_ADDRESS_DETAIL,
  COLUMN_RECEIVER_ADDRESS_DETAIL,
  COLUMN_SENDER_ADMIN_AREA,
  COLUMN_RECEIVER_ADMIN_AREA,
} from 'constants/new-bulk-upload-const';
import { getExcelReduxName } from 'components/v2/anytime-booking/common/new-bulk-upload-common';
import scribeNewBulkUploadDuck from 'ducks/scribe-new-bulk-upload-duck';
import { useDispatch } from 'react-redux';

import styles from './check-upload-table.less';
import { noBatchRowOrFirstRowInBatch } from './helper';

export const Batch = ({ row }) => {
  const { batch, sequenceNumber } = row;
  return <span>{batch ? `${batch} - ${sequenceNumber}` : undefined}</span>;
};

Batch.propTypes = {
  row: PropTypes.shape({}).isRequired,
};

export const NameAndPhone = ({ row, isPickUp, form }) => {
  const { id } = row;
  if (isPickUp && !noBatchRowOrFirstRowInBatch(row)) {
    return '--';
  }

  const nameField = isPickUp
    ? getExcelReduxName(COLUMN_SENDER_NAME)
    : getExcelReduxName(COLUMN_RECEIVER_NAME);
  const phoneField = isPickUp
    ? getExcelReduxName(COLUMN_SENDER_PHONE)
    : getExcelReduxName(COLUMN_RECEIVER_PHONE);

  return (
    <div className={styles.nameAndPhoneCol}>
      <InputField
        fieldClassName={styles.inputField}
        form={form}
        id={`${id}/${nameField}`}
        fieldOptions={{
          validateTrigger: 'onBlur',
          initialValue: _get(row, nameField),
          rules: deliveryFormFields.name.makeRules({ isPickUp }),
        }}
        autoComplete={'name'}
      />
      <InputField
        fieldClassName={styles.inputField}
        form={form}
        id={`${id}/${phoneField}`}
        fieldOptions={{
          validateTrigger: 'onBlur',
          initialValue: _get(row, phoneField),
          rules: deliveryFormFields.phone.makeRules({ isPickUp }),
        }}
        autoComplete={'phone'}
      />
    </div>
  );
};

NameAndPhone.propTypes = {
  row: PropTypes.shape({}).isRequired,
  isPickUp: PropTypes.bool.isRequired,
  form: PropTypes.shape({}).isRequired,
};

export const Address = ({
  row,
  isPickUp,
  setEditingAddress,
  form,
  hasWidthExpanded,
}) => {
  const { id, pickUpPOI, dropOffPOI } = row;
  const dispatch = useDispatch();

  const originalAddressName = isPickUp
    ? getExcelReduxName(COLUMN_SENDER_ADDRESS)
    : getExcelReduxName(COLUMN_RECEIVER_ADDRESS);
  const note = isPickUp
    ? getExcelReduxName(COLUMN_SENDER_ADDRESS_DETAIL)
    : getExcelReduxName(COLUMN_RECEIVER_ADDRESS_DETAIL);
  const adminArea = isPickUp
    ? getExcelReduxName(COLUMN_SENDER_ADMIN_AREA)
    : getExcelReduxName(COLUMN_RECEIVER_ADMIN_AREA);
  const originalAddressValue = _get(row, originalAddressName);
  const noteValue = _get(row, note);
  const adminAreaValue = _get(row, adminArea);
  const poi = isPickUp ? pickUpPOI : dropOffPOI;
  const display =
    _get(poi, ['address', 'combined_address']) ||
    originalAddressValue ||
    I18n.t('v2.error.uploadOrder.validationError.empty_field');
  const hasError = !_get(poi, 'id');

  const editAddress = useCallback(
    () => {
      setEditingAddress({
        rowID: id,
        isPickUp,
      });
      dispatch(
        scribeNewBulkUploadDuck.creators.scribeExpressNewBulkUploadStep2EditAddress(),
      );
      dispatch(
        scribeNewBulkUploadDuck.creators.scribeExpressNewBulkUploadStep2EditAddressDrawerDefault(
          isPickUp ? 'PICKUP' : 'DROPOFF',
          originalAddressValue,
          _get(poi, ['address', 'combined_address']),
          adminAreaValue,
        ),
      );
    },
    [
      setEditingAddress,
      id,
      isPickUp,
      poi,
      originalAddressValue,
      adminAreaValue,
    ],
  );

  if (isPickUp && !noBatchRowOrFirstRowInBatch(row)) {
    return '--';
  }

  return (
    <Popover
      overlayClassName={styles.addressPopoverOverlay}
      content={
        <div>
          <div className={styles.section}>
            <div className={styles.title}>
              {I18n.t(
                'v2.homepage.uploadOrder.reviewTable.action.originalAddress',
              )}
            </div>
            <div>
              {originalAddressValue ||
                I18n.t('v2.error.uploadOrder.validationError.empty_field')}
            </div>
          </div>
          <div className={styles.section}>
            <div className={styles.title}>
              {I18n.t('v2.homepage.uploadOrder.reviewTable.action.adjustedTo')}
            </div>
            <div>
              {_get(poi, ['address', 'combined_address']) ||
                I18n.t('v2.error.uploadOrder.validationError.empty_field')}{' '}
            </div>
            {noteValue && (
              <div className={styles.title} key={'title'}>
                {I18n.t('shopify.orders.addressDetails.locationDetails')}
              </div>
            )}
            {noteValue && (
              <div className={styles.value} key={'value'}>
                {noteValue}
              </div>
            )}
            <a onClick={editAddress} className={styles.action}>
              {I18n.t('v2.homepage.uploadOrder.reviewTable.action.edit')}
            </a>
          </div>
        </div>
      }
    >
      <div
        className={cx(styles.addressCol, {
          [styles.addressError]: hasError,
          [styles.addressColExpandedWidth]: hasWidthExpanded,
        })}
      >
        {display}
      </div>
      <DummyFieldError
        form={form}
        id={`${id}/${note}`}
        fieldOptions={{
          validateTrigger: 'onBlur',
          initialValue: noteValue,
          rules: deliveryFormFields.locationNote.makeRules({
            maxLength: deliveryFormFields.locationNote.maxLength,
          }),
        }}
      />
    </Popover>
  );
};

Address.propTypes = {
  row: PropTypes.shape({}).isRequired,
  isPickUp: PropTypes.bool.isRequired,
  setEditingAddress: PropTypes.func.isRequired,
  form: PropTypes.shape({}).isRequired,
  hasWidthExpanded: PropTypes.bool.isRequired,
};

export const CashOnDelivery = ({ row, form, codMeta, compulsoryCOD }) => {
  const currency = _get(codMeta, 'currency');
  if (!currency) {
    return <div className={styles.codCol}>--</div>;
  }

  const initialValue = _get(row, 'cod');
  const exponent = 10 ** _get(currency, 'exponent', 0);
  const lowerLimit = _get(codMeta, 'minValue', 0) / exponent;
  const upperLimit = _get(codMeta, 'maxValue', 0) / exponent;
  const hasPrefix = initialValue !== undefined && currency;
  return (
    <div className={styles.codCol}>
      {hasPrefix ? (
        <span className={styles.codSymbol}>{currency.symbol}</span>
      ) : (
        undefined
      )}
      <InputField
        form={form}
        type="number"
        id={`${row.id}/cod`}
        fieldOptions={{
          validateTrigger: 'onBlur',
          rules: deliveryFormFields.cod.makeRules({
            lowerLimit,
            upperLimit,
            compulsoryCOD,
          }),
          initialValue,
        }}
        fieldClassName={cx(styles.codField, {
          [styles.codFieldAligningError]: hasPrefix,
        })}
      />
    </div>
  );
};

CashOnDelivery.defaultProps = {
  compulsoryCOD: false,
};

CashOnDelivery.propTypes = {
  row: PropTypes.shape({}).isRequired,
  form: PropTypes.shape({}).isRequired,
  codMeta: PropTypes.shape({}).isRequired,
  compulsoryCOD: PropTypes.bool,
};

export const ServiceType = ({ row, metaData }) => {
  const { serviceType } = row;
  if (!noBatchRowOrFirstRowInBatch(row) || !serviceType) {
    return <div className={styles.serviceTypeCol}>--</div>;
  }
  const serviceTypeName = useMemo(
    () => _get(metaData, ['meta', 'serviceTypeNames', serviceType]),
    [metaData, serviceType],
  );

  return <div className={styles.serviceTypeCol}>{serviceTypeName}</div>;
};

ServiceType.propTypes = {
  row: PropTypes.shape({}).isRequired,
  metaData: PropTypes.shape({}).isRequired,
};

export const VehicleType = ({ row, metaData }) => {
  const { vehicleType } = row;
  if (!noBatchRowOrFirstRowInBatch(row) || !vehicleType) {
    return <div className={styles.vehicleTypeCol}>--</div>;
  }
  const vehicleTypeName = useMemo(
    () => _get(metaData, ['meta', 'vehicleTypeNames', vehicleType]),
    [metaData, vehicleType],
  );

  return <div className={styles.vehicleTypeCol}>{vehicleTypeName}</div>;
};

VehicleType.propTypes = {
  row: PropTypes.shape({}).isRequired,
  metaData: PropTypes.shape({}).isRequired,
};

export const Weight = ({ row, form, maxWeight }) => {
  const itemWeightOptions = useMemo(
    () => {
      const options = [];
      for (let i = 1; i <= maxWeight; i++) {
        options.push({
          value: i,
          child: <span className={styles.selectOption}>{i} kg</span>,
        });
      }
      return options;
    },
    [maxWeight],
  );

  return (
    <div className={styles.weightCol}>
      <SelectField
        className={styles.weightSelect}
        form={form}
        id={`${row.id}/itemWeight`}
        fieldOptions={{
          initialValue: _get(row, 'itemWeight'),
          rules: deliveryFormFields.itemWeight.makeRules({}),
        }}
        options={itemWeightOptions}
      />
    </div>
  );
};

Weight.propTypes = {
  row: PropTypes.shape({}).isRequired,
  form: PropTypes.shape({}).isRequired,
  maxWeight: PropTypes.number.isRequired,
};

export const Size = ({ row, form, parcelSizes }) => {
  const parcelSizeOptions = useMemo(
    () => {
      if (_isEmpty(parcelSizes)) {
        return [];
      }

      return parcelSizes.map(({ displaySize, length, width, height }) => ({
        value: displaySize,
        child: (
          <span>
            <strong>{displaySize}:</strong> L{length} x W{width} x H{height} cm
          </span>
        ),
      }));
    },
    [parcelSizes],
  );

  const parcelDisplaySizes = useMemo(
    () => {
      if (_isEmpty(parcelSizes)) {
        return [];
      }

      return parcelSizes.map(({ displaySize }) => displaySize);
    },
    [parcelSizes],
  );

  return (
    <div className={styles.weightCol}>
      <SelectField
        className={styles.weightSelect}
        dropdownClassName={styles.sizeDropdown}
        form={form}
        id={`${row.id}/parcelTotalSize`}
        fieldOptions={{
          initialValue: _get(row, 'parcelTotalSize'),
          rules: deliveryFormFields.parcelTotalSize.makeRules({
            parcelDisplaySizes,
          }),
        }}
        extraProps={{ optionLabelProp: 'value' }}
        options={parcelSizeOptions}
      />
    </div>
  );
};

Size.propTypes = {
  row: PropTypes.shape({}).isRequired,
  form: PropTypes.shape({}).isRequired,
  parcelSizes: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};
